import { type ReactNode } from 'react';
import 'react-confirm-alert/src/react-confirm-alert.css';
import history from '~/browserHistory';
import AnimatedCheckCircle from '../../components/shared/AnimatedCheckCircle';
import ConfirmPopup from '../../components/shared/ConfirmPopup';
import '../../themes/alertmessage.scss';
import Portal from '../Portal';

export interface ICreateJobPortalProps {
  /** The route to navigate to after user confirmation */
  route: string;
  /** Optional parameters to pass to the route */
  params?: unknown;
  /** Callback function called when user confirms the success message */
  submitPressed: () => void;
}

/**
 * CreateJobPortal Component
 *
 * A success confirmation modal that appears after job creation, displaying
 * a checkmark animation and success message with navigation to a specified route.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for job creation success confirmation in App.js.
 * Consider whether this should be moved to a more specific location like:
 * - src/components/jobs/CreateJobSuccessModal
 * - src/components/modals/CreateJobSuccessModal
 *
 * If this is truly only used for job creation workflow, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <CreateJobPortal
 *   route="/jobs"
 *   params={{ id: 123 }}
 *   submitPressed={() => console.log('Job created')}
 * />
 * ```
 */
export const CreateJobPortal = ({
  route,
  params,
  submitPressed,
}: ICreateJobPortalProps): ReactNode => {
  return (
    <Portal>
      {/* @ts-expect-error - ConfirmPopup is JS component without proper TypeScript types */}
      <ConfirmPopup
        Icon={() => <AnimatedCheckCircle />}
        title="Request Successfully Submitted!"
        titleStyle={{ marginTop: 20 }}
        description="Your shift is now available for professionals nearby. Check back for new candidates."
        descriptionStyle={{ width: '90%' }}
        rightButtonText="Done"
        rightButtonAction={() => {
          submitPressed();
          history.push(route, params);
        }}
      />
    </Portal>
  );
};
